import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, Field } from "@/components/common";
import CompanySelect from "@/components/CompanySelect";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, pretty, ROLE_LABEL } from "@/lib/api";
import PermissionsDialog from "@/components/PermissionsDialog";
import EmailHandoverDialog from "@/components/EmailHandoverDialog";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const STATUS_CLS = { active: "text-emerald-700", invited: "text-amber-700", deactivated: "text-red-600" };

function inviteRoles(role) {
  if (role === "admin") return ["client_owner", "client_contributor", "staff", "admin"];
  if (role === "staff") return ["client_owner", "client_contributor"];
  return ["client_contributor"];
}

export function InviteLink({ link, userId }) {
  if (!link) return null;
  return (
    <div className="rounded-md border bg-muted/50 p-3 text-xs" data-testid="invite-link-box">
      <p className="mb-1 font-semibold">Invite link (also emailed)</p>
      <div className="flex items-center gap-2"><code className="truncate" data-testid="invite-link-value">{link}</code>
        <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copied"); }} data-testid="invite-link-copy"><Copy className="h-3.5 w-3.5" /></Button>
        {userId && <WhatsAppButton kind="invite" id={userId} label="Send Invitation via WhatsApp" />}</div>
    </div>
  );
}

function InviteDialog({ tenantId, onClose, onDone }) {
  const { user, isStaff } = useAuth();
  const roles = inviteRoles(user.role);
  const [f, setF] = useState({ email: "", name: "", phone: "", role: roles[0], tenant_id: tenantId || "", job_role: "" });
  const [cat, setCat] = useState(null);
  useEffect(() => { api.get("/access/catalog").then((r) => setCat(r.data)); }, []);
  const [link, setLink] = useState(null);
  const [newId, setNewId] = useState(null);
  const submit = async () => {
    try {
      const { data } = await api.post("/invites", { ...f, phone: f.phone || null, tenant_id: f.tenant_id || null, job_role: f.job_role || null });
      toast.success(data.email_sent ? "Invitation emailed" : "User created — email could not be sent, share the link");
      setLink(data.invite_link); setNewId(data.user?.id); onDone();
      if (!data.invite_link) onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const clientRole = f.role.startsWith("client");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader><DialogTitle>Invite user</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Role">
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {cat && f.role !== "admin" && <Field label="Job role (sets default permissions)"><Select value={f.job_role || undefined} onValueChange={(v) => setF({ ...f, job_role: v })}>
            <SelectTrigger data-testid="invite-job-select"><SelectValue placeholder="Default for this category" /></SelectTrigger>
            <SelectContent>{Object.entries(clientRole ? cat.client_roles : cat.staff_roles).filter(([k]) => k !== "va_admin" && (f.role !== "client_contributor" || k !== "company_admin")).map(([k, r]) => <SelectItem key={k} value={k} data-testid={`invite-job-${k}`}><div><div>{r.label}</div>{(r.desc || r.purpose) && <div className="max-w-[360px] whitespace-normal text-xs text-muted-foreground">{r.desc || r.purpose}</div>}</div></SelectItem>)}</SelectContent></Select></Field>}
          {isStaff && clientRole && !tenantId && <Field label="Company"><CompanySelect value={f.tenant_id} onChange={(v) => setF({ ...f, tenant_id: v })} testId="invite-company-select" /></Field>}
          <Field label="Full name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name-input" /></Field>
          <Field label="Email"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email-input" /></Field>
          <Field label="WhatsApp number (optional)"><Input placeholder="+255…" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="invite-phone-input" /></Field>
          <InviteLink link={link} userId={newId} />
        </div>
        <DialogFooter>{link ? <Button variant="outline" onClick={onClose} data-testid="invite-done-btn">Done</Button> : <Button onClick={submit} data-testid="invite-submit-btn">Send invitation</Button>}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccessDialog({ target, onClose, onDone }) {
  const [tid, setTid] = useState("");
  const [role, setRole] = useState("client_contributor");
  const save = async () => {
    try { await api.post(`/users/${target.id}/memberships`, { tenant_id: tid, role }); toast.success("Company access granted"); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="membership-dialog">
        <DialogHeader><DialogTitle>Grant company access · {target.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Current: {(target.memberships || []).map((m) => m.tenant_id === target.tenant_id ? `${target.company_name} (default)` : m.tenant_id.slice(0, 8)).join(", ") || target.company_name}</p>
        <Field label="Company"><CompanySelect value={tid} onChange={setTid} testId="membership-company-select" /></Field>
        <Field label="Role in that company">
          <Select value={role} onValueChange={setRole}><SelectTrigger data-testid="membership-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["client_owner", "client_contributor"].map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent></Select>
        </Field>
        <DialogFooter><Button disabled={!tid} onClick={save} data-testid="membership-save-btn">Grant access</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPanel({ tenantId, scope }) {
  const { user, isStaff } = useAuth();
  const [granting, setGranting] = useState(null);
  const [perms, setPerms] = useState(null);
  const [emailUser, setEmailUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [link, setLink] = useState(null);
  const keep = (u) => !scope || (scope === "staff") === !u.role.startsWith("client");
  const load = () => api.get("/users", { params: { tenant_id: tenantId || undefined } }).then((r) => setUsers(r.data.filter(keep))).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [tenantId, scope]); // eslint-disable-line
  const act = async (u, action) => {
    if (action === "deactivate" && !window.confirm(`Deactivate ${u.name}? Their audit history is kept.`)) return;
    try {
      if (action === "resend") { const { data } = await api.post(`/invites/${u.id}/resend`); setLink(data.invite_link); toast.success("Invitation re-sent"); }
      else { await api.post(`/users/${u.id}/${action}`); toast.success(`User ${action}d`); }
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const canManage = (u) => u.id !== user.id && (user.role === "admin" || (user.role === "staff" && u.role.startsWith("client") && user.perms?.users_admin) || (!isStaff && user.perms?.team_manage && u.role.startsWith("client")));
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => setInviting(true)} data-testid="invite-user-btn"><UserPlus className="mr-1 h-4 w-4" /> Invite user</Button></div>
      <InviteLink link={link} />
      {!users.length ? <EmptyState title="No users yet" testId="users-empty" /> : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full min-w-[640px] text-sm" data-testid="users-table">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Since</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3"><div className="font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div></td>
                  <td className="px-4 py-3">{ROLE_LABEL[u.role]}<div className="text-xs text-muted-foreground" data-testid={`user-job-${u.id}`}>{pretty(((u.memberships || []).find((m) => m.tenant_id === (tenantId || u.tenant_id)) || {}).job_role || u.job_role || "")}</div></td>
                  <td className="px-4 py-3 text-muted-foreground">{u.company_name || "V.A"}</td>
                  <td className={`px-4 py-3 font-semibold capitalize ${STATUS_CLS[u.status]}`} data-testid={`user-status-${u.id}`}>{u.status}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {canManage(u) && u.role !== "admin" && <Button size="sm" variant="ghost" onClick={() => setPerms(u)} data-testid={`user-access-${u.id}`}>Access</Button>}
                    {isStaff && u.role.startsWith("client") && u.status !== "deactivated" && <Button size="sm" variant="ghost" onClick={() => setGranting(u)} data-testid={`user-grant-company-${u.id}`}>+ Company</Button>}
                    {user.role === "admin" && u.role.startsWith("client") && u.status === "active" && <Button size="sm" variant="ghost" onClick={() => setEmailUser(u)} data-testid={`user-email-handover-${u.id}`}>Email & handover</Button>}
                    {canManage(u) && u.status === "invited" && <Button size="sm" variant="ghost" onClick={() => act(u, "resend")} data-testid={`user-resend-${u.id}`}>Resend</Button>}
                    {u.status === "invited" && <WhatsAppButton kind="invite" id={u.id} label="WhatsApp invite" variant="ghost" />}
                    {canManage(u) && u.status !== "deactivated" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => act(u, "deactivate")} data-testid={`user-deactivate-${u.id}`}>Deactivate</Button>}
                    {canManage(u) && u.status === "deactivated" && <Button size="sm" variant="ghost" onClick={() => act(u, "reactivate")} data-testid={`user-reactivate-${u.id}`}>Reactivate</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {inviting && <InviteDialog tenantId={tenantId} onClose={() => setInviting(false)} onDone={load} />}
      {granting && <AccessDialog target={granting} onClose={() => setGranting(null)} onDone={load} />}
      {perms && <PermissionsDialog target={perms} tenantId={tenantId} onClose={() => setPerms(null)} onDone={load} />}
      {emailUser && <EmailHandoverDialog u={emailUser} onClose={() => setEmailUser(null)} onDone={load} />}
    </div>
  );
}
